/** @jsx jsx */
import type { WithDataAttributeProp } from '@balance-web/core';
import { getTestIdFromDataAttributes, jsx } from '@balance-web/core';
import { Flex } from '@balance-web/flex';
import { Stack } from '@balance-web/stack';
import { Text } from '@balance-web/text';
import { theme } from '@balance-web/theme';
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
} from '@balance-web/icon';
import type { ComponentProps, ReactNode } from 'react';
import {
  KeyGroup,
  OS_ALT_KEY,
  OS_CTRL_KEY,
} from '@balance-web/keyboard-shortcuts';
import { makeIdFactory } from '@balance-web/utils';

const KeyboardTestIdFactory = makeIdFactory('keyboard-shortcuts', ['legend']);

export const KeyboardShotcutsLegend = (
  props: WithDataAttributeProp<ComponentProps<'div'>>
) => {
  const testIds = KeyboardTestIdFactory(
    getTestIdFromDataAttributes(props.data)
  );

  return (
    <Stack
      data-testid={testIds.base(props.id)}
      gap="large"
      height={348}
      overflowY="auto"
      paddingBottom="medium"
      paddingX="large"
    >
      <Text
        css={{
          position: 'sticky',
          top: 0,
          left: 0,
          background: theme.semantic.colour.surface.neutral.background,
          paddingTop: theme.spacing.medium,
        }}
        size="small"
        weight="semibold"
      >
        Keyboard shortcuts
      </Text>
      <Stack gap="small">
        <KeyboardShortcut keys={['T']} label="Today" />
        <KeyboardShortcut keys={['W']} label="First day of the week" />
        <KeyboardShortcut keys={['K']} label="Last day of the week" />
        <KeyboardShortcut keys={['M']} label="First day of the month" />
        <KeyboardShortcut keys={['H']} label="Last day of the month" />
        <KeyboardShortcut
          keys={[OS_ALT_KEY, <ArrowLeftIcon key="arrow-left" size="xsmall" />]}
          label="Previous day"
        />
        <KeyboardShortcut
          keys={[
            OS_ALT_KEY,
            <ArrowRightIcon key="arrow-right" size="xsmall" />,
          ]}
          label="Next day"
        />
        <KeyboardShortcut
          keys={[OS_ALT_KEY, <ArrowUpIcon key="arrow-up" size="xsmall" />]}
          label="Previous week"
        />
        <KeyboardShortcut
          keys={[OS_ALT_KEY, <ArrowDownIcon key="arrow-down" size="xsmall" />]}
          label="Next week"
        />

        <KeyboardShortcut
          keys={[
            OS_ALT_KEY,
            'Shift',
            <ArrowUpIcon key="arrow-up" size="xsmall" />,
          ]}
          label="Previous month"
        />
        <KeyboardShortcut
          keys={[
            OS_ALT_KEY,
            'Shift',
            <ArrowDownIcon key="arrow-down" size="xsmall" />,
          ]}
          label="Next month"
        />

        <KeyboardShortcut
          keys={[
            OS_CTRL_KEY,
            'Shift',
            <ArrowUpIcon key="arrow-up" size="xsmall" />,
          ]}
          label="Previous year"
        />
        <KeyboardShortcut
          keys={[
            OS_CTRL_KEY,
            'Shift',
            <ArrowDownIcon key="arrow-down" size="xsmall" />,
          ]}
          label="Next year"
        />
        <KeyboardShortcut keys={['Y']} label="First day of the year" />
        <KeyboardShortcut keys={['R']} label="Last day of the year" />
      </Stack>
    </Stack>
  );
};

const KeyboardShortcut = (props: { label: string; keys: Array<ReactNode> }) => {
  return (
    <Flex alignItems="center" gap="large" justifyContent="space-between">
      <Text size="xsmall" weight="medium">
        {props.label}
      </Text>
      <KeyGroup keys={props.keys} size="xsmall" variant="tag" />
    </Flex>
  );
};
